// src/api/queries/assignment-approvals.js
/**
 * Хуки согласования назначений. GET /assignment-approvals,
 * POST /:id/decide { action, reason, newAssigneeId }.
 *
 * useDecideAssignmentApprovalMutation.onSuccess инвалидирует:
 *   - ["assignment-approvals"] — список;
 *   - ["tasks"] — approve/reassign меняют tasks.status/assignee_id
 *     (см. AssignmentApprovalService._promoteTarget/_resetTarget);
 *   - ["projects"] — то же для project.manager_id/status;
 *   - ["task"] и ["project"] по префиксу — открытые карточки (TaskModal/
 *     ProjectModal держат assigneeId/status из своего кэша ["task", id]/
 *     ["project", id]; без префиксной инвалидации UI показывает
 *     устаревшего исполнителя до закрытия/перезагрузки);
 *   - ["reports"] — approve/reassign меняют исполнителя, а значит и
 *     загрузку сотрудников; префиксный ключ покрывает /workload,
 *     /period-capacity, /employee-tasks;
 *   - ["notifications"] — уведомления о решении.
 *
 * POST /assignment-approvals (force-request) на фронте не используется:
 * requestIfNeeded вызывается автоматически из TaskService/ProjectService.
 * Хук не добавляем (мёртвый код «на будущее» — антипаттерн).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export const ASSIGNMENT_APPROVALS_KEY = ["assignment-approvals"];

export function useAssignmentApprovalsQuery(options = {}) {
  return useQuery({
    queryKey: ASSIGNMENT_APPROVALS_KEY,
    queryFn: () => api.get("/assignment-approvals"),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useDecideAssignmentApprovalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, reason, newAssigneeId, __idempotencyKey }) => {
      const headers = {};
      if (__idempotencyKey) headers["Idempotency-Key"] = __idempotencyKey;
      return api.post(
        `/assignment-approvals/${id}/decide`,
        { action, reason, newAssigneeId },
        { headers },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ASSIGNMENT_APPROVALS_KEY });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["task"] });
      qc.invalidateQueries({ queryKey: ["project"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
